/**
 * FASE 21.2 — Visits Mutation Adapter
 *
 * Wraps the existing trusted API layer.
 * Never accesses Supabase directly — delegates to api.js functions
 * so offline outbox, cache invalidation, and audit logging all work.
 */

import { createVisit, updateVisit, completeVisit } from "../../api";
import { generateUUID } from "../../uuid";

/**
 * Create a new pastoral visit.
 * @param {Object} payload — validated by validateMutationPayload
 * @param {Object} appScope — from KeviaContext (district_id, church_id, role)
 * @returns {Promise<{ ok: boolean, entityId: string|null, summary: string, meta: Object }>}
 */
export async function createVisitMutation(payload, appScope) {
  const now = Date.now();

  // Build typed write payload — only known fields
  const writePayload = {
    id:                 generateUUID(),
    contact_name:       payload.contact_name,
    visit_type:         payload.visit_type ?? "pastoral",
    date:               payload.date,
    time:               payload.time ?? null,
    status:             "pendiente",
    district_id:        payload.district_id ?? appScope?.district_id ?? null,
    church_id:          payload.church_id   ?? appScope?.church_id   ?? null,
    target_family_id:   payload.target_family_id ?? null,
    target_person_id:   payload.target_person_id ?? null,
    created_by_kevia:   true,
  };

  const data = await createVisit(writePayload);
  return {
    ok:       true,
    entityId: data?.id ?? writePayload.id,
    summary:  `Visita registrada para ${writePayload.contact_name} (${writePayload.date}).`,
    meta:     { latency: Date.now() - now, auditType: "pastoral_action" },
  };
}

/**
 * Schedule a followup visit.
 * Internally creates a visit with status "pendiente" and the followup date.
 */
export async function scheduleFollowupMutation(payload, appScope) {
  const now = Date.now();
  const writePayload = {
    id:               generateUUID(),
    contact_name:     payload.contact_name,
    visit_type:       payload.visit_type ?? "seguimiento",
    date:             payload.followup_date,
    status:           "pendiente",
    district_id:      payload.district_id ?? appScope?.district_id ?? null,
    church_id:        payload.church_id   ?? appScope?.church_id   ?? null,
    target_family_id: payload.target_family_id ?? null,
    target_person_id: payload.target_person_id ?? null,
    created_by_kevia: true,
  };

  const data = await createVisit(writePayload);
  return {
    ok:       true,
    entityId: data?.id ?? writePayload.id,
    summary:  `Seguimiento programado para ${writePayload.contact_name} el ${writePayload.date}.`,
    meta:     { latency: Date.now() - now, auditType: "pastoral_planning" },
  };
}

/**
 * Update a visit's status.
 */
export async function updateVisitStatusMutation(payload, appScope) {
  const now = Date.now();
  const updates = {
    status:      payload.new_status,
    district_id: payload.district_id ?? appScope?.district_id ?? null,
  };
  if (payload.new_status === "realizada") {
    updates.completed_at = new Date().toISOString();
  }
  await updateVisit(payload.visit_id, updates);
  return {
    ok:       true,
    entityId: payload.visit_id,
    summary:  `Visita marcada como "${payload.new_status}".`,
    meta:     { latency: Date.now() - now, auditType: "status_change" },
  };
}
